import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Search, MessageSquare, X, Eye, EyeOff, Users } from "lucide-react";
import DirectChat from "@/components/DirectChat";
import GroupChat from "@/components/GroupChat";
import { AnnouncementDialog } from "@/components/AnnouncementDialog";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { chatStamp, crDateTime } from "@/lib/dateFormat";
import { toast } from "sonner";

export default function Messages() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [groupChats, setGroupChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [showHidden, setShowHidden] = useState(false);

  const load = async () => {
    try {
      const [r, g] = await Promise.all([
        api.get("/dm/threads", { params: { include_hidden: showHidden } }),
        api.get("/chat/groups").catch(() => ({ data: [] })),
      ]);
      setThreads(r.data || []);
      setGroupChats(g.data || []);
      // Only auto-select the first thread the very first time (avoid stealing
      // focus on subsequent polls that would otherwise "jump" the user back).
      setSelected((prev) => prev || ((r.data || [])[0] || null));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHidden]);

  const toggleHide = async (th, hide) => {
    try {
      if (hide) {
        await api.post(`/dm/${th.peer_id}/hide`);
        if (selected?.peer_id === th.peer_id) setSelected(null);
        toast.success("Conversación oculta");
      } else {
        await api.delete(`/dm/${th.peer_id}/hide`);
        toast.success("Conversación mostrada");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = threads.filter((th) =>
    !search || (th.peer_name || "").toLowerCase().includes(search.toLowerCase())
    || (th.peer_email || "").toLowerCase().includes(search.toLowerCase())
  );

  const gruposFiltrados = groupChats.filter((g) =>
    !search || (g.group_name || "").toLowerCase().includes(search.toLowerCase()));

  const SECCIONES = ["staff", "teachers", "students", "affiliates"];
  const porSeccion = SECCIONES
    .map((s) => [s, filtered.filter((th) => (th.section || "staff") === s)])
    .filter(([, list]) => list.length > 0);

  return (
    <div data-testid="messages-page">
      <PageHeader
        title={t("messages")}
        subtitle={t(user?.live_pass?.status ? "messages_subtitle_pass" : "messages_subtitle")}
        testId="messages-header"
        action={<AnnouncementDialog role={user?.role} onSent={load} />}
      />

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
      ) : threads.length === 0 && groupChats.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">{t("no_messages_available")}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* Threads list */}
          <Card className="p-0 overflow-hidden max-h-[70vh] flex flex-col">
            <div className="p-3 border-b border-slate-200 bg-slate-50/50 space-y-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("search")}
                  className="pl-8 h-9 text-sm"
                  data-testid="dm-search"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowHidden((v) => !v)}
                className="text-[11px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                data-testid="dm-toggle-hidden"
              >
                {showHidden ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                {showHidden ? "Ocultar los ocultos" : "Mostrar conversaciones ocultas"}
              </button>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-slate-100" data-testid="dm-thread-list">
              {gruposFiltrados.length > 0 && (
                <div data-testid="dm-section-groups">
                  <div className="px-3 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t("gc_section")} · {gruposFiltrados.length}
                  </div>
                  {gruposFiltrados.map((g) => {
                    const active = selected?.group_id === g.group_id;
                    return (
                      <button key={g.group_id} type="button" onClick={() => setSelected({ ...g, is_group: true })}
                              data-testid={`gc-thread-${g.group_id}`}
                              className={`w-full text-left p-3 flex items-start gap-3 min-w-0 hover:bg-slate-50 transition-colors ${active ? "bg-emerald-50/60" : ""}`}>
                        <span className="h-10 w-10 shrink-0 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
                          <Users className="h-5 w-5" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center justify-between gap-2">
                            <span className={`text-sm truncate ${g.unread_count > 0 ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
                              {g.group_name}
                            </span>
                            {g.unread_count > 0 && (
                              <span className="bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                {g.unread_count}
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-slate-500 truncate mt-0.5">
                            {g.last_text ? `${g.last_author ? g.last_author + ": " : ""}${g.last_text}` : <em className="italic text-slate-400">{t("no_messages_yet")}</em>}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {porSeccion.map(([sec, list]) => (
                <div key={sec} data-testid={`dm-section-${sec}`}>
                  <div className="px-3 py-1.5 bg-slate-50 border-y border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t(`dm_sec_${sec}`)} · {list.length}
                  </div>
                  {list.map((th) => {
                const active = selected?.peer_id === th.peer_id;
                return (
                  <div key={th.peer_id} className={`group relative w-full flex items-start hover:bg-slate-50 transition-colors ${active ? "bg-emerald-50/60" : ""} ${th.is_hidden ? "opacity-60" : ""}`}>
                    <button
                      type="button"
                      onClick={() => setSelected(th)}
                      data-testid={`dm-thread-${th.peer_id}`}
                      className="flex-1 text-left p-3 flex items-start gap-3 min-w-0"
                    >
                      <BrandAvatar src={th.peer_picture} alt={th.peer_name} className="h-10 w-10 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm truncate ${th.unread_count > 0 ? "font-bold text-slate-900" : "font-medium text-slate-800"}`}>
                            {th.peer_name}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {th.last_at && (
                              <span className="text-[10px] text-slate-400" title={crDateTime(th.last_at, lang)} data-testid={`dm-thread-date-${th.peer_id}`}>
                                {chatStamp(th.last_at, lang)}
                              </span>
                            )}
                            {th.unread_count > 0 && (
                              <span className="bg-emerald-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                                {th.unread_count}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {th.last_text || <em className="italic text-slate-400">{t("no_messages_yet")}</em>}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleHide(th, !th.is_hidden); }}
                      className="absolute right-2 top-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-600 transition-opacity"
                      title={th.is_hidden ? "Mostrar de nuevo" : "Ocultar de mi lista"}
                      data-testid={`dm-hide-${th.peer_id}`}
                    >
                      {th.is_hidden ? <Eye className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    </button>
                  </div>
                );
              })}
                </div>
              ))}
              {filtered.length === 0 && (
                <div className="p-6 text-center text-xs text-slate-400 italic">{t("no_results")}</div>
              )}
            </div>
          </Card>

          {/* Chat pane */}
          <Card className="p-3 flex flex-col overflow-hidden">
            {!selected ? (
              <div className="flex items-center justify-center flex-1 py-16 text-sm text-slate-400 italic">
                {t("pick_conversation")}
              </div>
            ) : selected.is_group ? (
              <>
                <div className="flex items-center gap-3 pb-3 border-b border-slate-200 mb-2">
                  <span className="h-10 w-10 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
                    <Users className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate" data-testid="gc-chat-group-name">
                      {selected.group_name}
                    </div>
                    <div className="text-[11px] text-slate-500">{t("gc_subtitle")}</div>
                  </div>
                </div>
                <GroupChat group={selected} />
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 pb-3 border-b border-slate-200 mb-2">
                  <BrandAvatar src={selected.peer_picture} alt={selected.peer_name} className="h-10 w-10" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate" data-testid="dm-chat-peer-name">{selected.peer_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{selected.peer_email}</div>
                  </div>
                  {selected.peer_whatsapp && (
                    <a
                      href={`https://wa.me/${(selected.peer_whatsapp || "").replace(/[^\d]/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-emerald-700 hover:underline"
                      data-testid="dm-open-whatsapp"
                    >
                      WhatsApp
                    </a>
                  )}
                </div>
                <DirectChat peer={selected} />
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
